/**
 * Button Component
 * A versatile button component with multiple variants and sizes.
 * @packageDocumentation
 */

import { Loader2 } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ReactNode, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/** Button style variants using class-variance-authority. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer rounded text-sm font-medium transition-all duration-200 ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        lg: 'h-10 rounded px-8',
        'icon-sm': 'h-7 w-7 rounded',
        default: 'h-[36px] px-4 py-2',
        sm: 'h-8 rounded px-3 text-xs',
        icon: 'h-[36px] w-[36px] rounded [&_svg]:size-[18px]',
        'icon-xs': 'h-auto min-h-6 min-w-6 w-auto rounded p-1 [&_svg]:size-3.5',
      },
      variant: {
        link: 'text-primary underline-offset-4 hover:underline',
        ghost: 'hover:bg-accent/80 hover:text-accent-foreground',
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md',
        success: 'bg-success text-success-foreground shadow-sm hover:bg-success/90 hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md',
        secondary:
          'bg-card text-secondary-foreground shadow-sm hover:bg-muted/80 border border-transparent hover:border-border/50',
        outline:
          'border border-input bg-card shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20',
      },
    },
  },
);

/**
 * Props for the Button component.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as a child component using Slot. */
  asChild?: boolean;
  /** Show loading spinner. */
  loading?: boolean;
  /** Icon to display on the left. */
  leftIcon?: ReactNode;
  /** Icon to display on the right. */
  rightIcon?: ReactNode;
}

/**
 * A button component with multiple variants, sizes, and loading state.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { size, variant, leftIcon, disabled, children, className, rightIcon, asChild = false, loading = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          buttonVariants({
            size,
            variant,
            className,
          }),
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
